/**
 * CompanyAlbum — masonry-style photo grid showcasing Guard's work in Lagos.
 *
 * Stock photography sourced from Unsplash (free for commercial use,
 * no attribution required) — credits documented inline so legal +
 * design can rotate them later without hunting through markup.
 *
 * The visual rhythm is asymmetric on desktop (one tall + four squares +
 * one wide) — closer to an editorial layout than a generic e-commerce
 * tile grid; matches Guard's sharp-edged design language.
 */
import { Camera } from "lucide-react";

const ALBUM = [
  {
    id: "lagos-aerial",
    src: "https://images.unsplash.com/photo-1618828665011-0abd973f7bb8?fm=jpg&q=70&w=1400&auto=format&fit=crop",
    alt: "Aerial view of Lagos Island skyline",
    caption: "The neighbourhoods we monitor — every alert geo-tagged, every minute counted.",
    credit: "Photo: Nupo Deyon Daniel (Unsplash)",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    id: "lagos-traffic",
    src: "https://images.unsplash.com/photo-1648023199223-25d3622bcb13?fm=jpg&q=70&w=900&auto=format&fit=crop",
    alt: "Busy Lagos street with everyday traffic",
    caption: "eCaution road reports keep commuters moving.",
    credit: "Photo: Opeyemi Adisa (Unsplash)",
    span: "",
  },
  {
    id: "lagos-bridge",
    src: "https://images.unsplash.com/photo-1648023200201-8fcede127835?fm=jpg&q=70&w=900&auto=format&fit=crop",
    alt: "Lekki–Ikoyi bridge over Lagos lagoon at night",
    caption: "24/7 incident response, even through the night shift.",
    credit: "Photo: Opeyemi Adisa (Unsplash)",
    span: "",
  },
  {
    id: "community",
    src: "https://images.unsplash.com/photo-1572816225927-d08fb138f2b2?fm=jpg&q=70&w=900&auto=format&fit=crop",
    alt: "People and vehicles on a Lagos street during daytime",
    caption: "Community-first: neighbours verifying neighbours.",
    credit: "Photo: Muhammad-Taha Ibrahim (Unsplash)",
    span: "",
  },
  {
    id: "city-night",
    src: "https://images.unsplash.com/photo-1593717191400-84f38ee95485?fm=jpg&q=70&w=900&auto=format&fit=crop",
    alt: "Time-lapse of city lights in Lagos at night",
    caption: "Live tracking + push alerts — distance is no longer an excuse.",
    credit: "Photo: Onaopemipo Oladipupo (Unsplash)",
    span: "",
  },
];

export default function CompanyAlbum() {
  return (
    <section
      className="bg-white border-y border-gray-200 px-6 sm:px-12 py-14"
      data-testid="company-album"
      aria-labelledby="album-heading"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Camera className="w-5 h-5 text-red-700" strokeWidth={2} />
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-red-700 font-body">
            Field Album
          </span>
        </div>
        <h2 id="album-heading" className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-gray-900 mb-3">
          From the field
        </h2>
        <p className="font-body text-sm sm:text-base text-gray-600 max-w-2xl mb-8">
          A glimpse of our work powering emergency response, ePass premise security, and stolen-vehicle recovery
          across Lagos and the federation.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {ALBUM.map((item) => (
            <figure
              key={item.id}
              className={`relative group rounded-sm overflow-hidden border border-gray-200 bg-gray-100 ${item.span}`}
              data-testid={`album-tile-${item.id}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="w-full h-full min-h-[180px] object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3 sm:p-4">
                <p className="text-white font-body text-xs sm:text-sm leading-snug">{item.caption}</p>
                <p className="text-white/60 text-[10px] mt-1 font-body italic">{item.credit}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
